import { ComboboxData, ComboboxItem, ComboboxItemGroup } from '@mantine/core'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import clearIcon from '~/widgets/CostBomDemo/assets/images/x.svg'
import { COST_BOM_DEMO_STRINGS } from '~/widgets/CostBomDemo/strings'
import { COUNTRY_OPTIONS } from '~/widgets/TariffCalculator/utils'

type CountryEntry = { group?: string; value: string }

function flattenCountryOptions(data: ComboboxData): CountryEntry[] {
  return data.flatMap((item): CountryEntry[] => {
    if (typeof item === 'string') {
      return [{ value: item }]
    }

    if ('group' in item) {
      const group = item as ComboboxItemGroup
      return group.items.map((sub) => ({
        group: group.group,
        value: typeof sub === 'string' ? sub : (sub as ComboboxItem).value,
      }))
    }

    return [{ value: (item as ComboboxItem).value }]
  })
}

const ALL_COUNTRIES = flattenCountryOptions(COUNTRY_OPTIONS)

interface HeroCountrySelectProps {
  value: string
  onChange: (value: string) => void
  error?: boolean
}

export function HeroCountrySelect({
  value: country,
  onChange,
  error = false,
}: HeroCountrySelectProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const closeList = useCallback((blur = true) => {
    setOpen(false)
    setQuery('')
    if (blur) inputRef.current?.blur()
  }, [])

  const syncClosedState = useCallback(() => {
    setOpen(false)
    setQuery('')
  }, [])

  useEffect(() => {
    const onDocPointer = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!wrapRef.current?.contains(target)) {
        closeList()
        return
      }

      const onFieldSurface =
        target === inputRef.current ||
        !!target.closest(
          '.hero-conv-country-pop, .hero-conv-country-opt, .hero-conv-country-clear-btn, .hero-conv-country-caret-btn',
        )

      if (!onFieldSurface) {
        closeList()
      }
    }
    document.addEventListener('mousedown', onDocPointer, true)
    return () => document.removeEventListener('mousedown', onDocPointer, true)
  }, [closeList])

  const filteredCountries = useMemo(() => {
    const q = query.trim().toLowerCase()
    const list = q
      ? ALL_COUNTRIES.filter((entry) => entry.value.toLowerCase().includes(q))
      : ALL_COUNTRIES

    if (q) {
      return list.map((entry) => ({ ...entry, showHeader: false }))
    }

    let lastGroup: string | undefined
    return list.map((entry) => {
      const showHeader = !!entry.group && entry.group !== lastGroup
      if (showHeader) lastGroup = entry.group
      return { ...entry, showHeader }
    })
  }, [query])

  const pickCountry = (value: string) => {
    onChange(value)
    closeList()
  }

  const clearCountry = () => {
    onChange('')
    setQuery('')
    setOpen(true)
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  const openList = () => {
    setQuery(country)
    setOpen(true)
  }

  const handleInputBlur = () => {
    window.setTimeout(() => {
      if (!wrapRef.current?.contains(document.activeElement)) {
        syncClosedState()
      }
    }, 0)
  }

  return (
    <div ref={wrapRef} className={`hero-conv-country${open ? ' open' : ''}`}>
      <div className="hero-conv-country-input-wrap">
        <input
          ref={inputRef}
          type="text"
          id="hero-conv-country"
          className={`mi-cost_model-email_field hero-conv-country-input w-input${
            country ? ' hero-conv-country-input--has-clear' : ''
          }${error ? ' err' : ''}`}
          value={open ? query : country}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={openList}
          onBlur={handleInputBlur}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls="hero-conv-country-listbox"
          aria-label={COST_BOM_DEMO_STRINGS.sourcingFromLabel}
          autoComplete="off"
          role="combobox"
        />
        <div className="hero-conv-country-trailing">
          {country ? (
            <button
              type="button"
              className="hero-conv-country-clear-btn"
              tabIndex={-1}
              aria-label={COST_BOM_DEMO_STRINGS.clearCountry}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation()
                clearCountry()
              }}
            >
              <img src={clearIcon} alt="" width={12} height={12} />
            </button>
          ) : null}
          <button
            type="button"
            className="hero-conv-country-caret-btn"
            tabIndex={-1}
            aria-label={COST_BOM_DEMO_STRINGS.searchCountries}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation()
              if (open) {
                closeList()
              } else {
                inputRef.current?.focus()
              }
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 6l4 4 4-4"
                stroke="#868E96"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <div
        id="hero-conv-country-listbox"
        className="hero-conv-country-pop"
        hidden={!open}
        role="listbox"
        aria-label={COST_BOM_DEMO_STRINGS.sourcingFromLabel}
      >
        <div className="hero-conv-country-list">
          {filteredCountries.length === 0 ? (
            <div className="hero-conv-country-empty">
              {COST_BOM_DEMO_STRINGS.noCountriesFound}
            </div>
          ) : (
            filteredCountries.map((entry) => (
              <div key={`${entry.group ?? ''}-${entry.value}`}>
                {entry.showHeader && entry.group && (
                  <div className="hero-conv-country-group" role="presentation">
                    {entry.group}
                  </div>
                )}
                <button
                  type="button"
                  role="option"
                  aria-selected={country === entry.value}
                  className={`hero-conv-country-opt${
                    country === entry.value ? ' active' : ''
                  }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation()
                    pickCountry(entry.value)
                  }}
                >
                  {entry.value}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
